// Events API Adapter
// ALWAYS calls live API - NO silent mock fallback in production
// Supports: ?source=mock (explicit) | ?source=neon (default in prod/staging)

import { getDataSource, getApiBaseUrl } from './config';
import apiClient from './client';

/**
 * Track if Neon mode failed - used for warning banner
 */
let neonFailedFlag = false;
let neonErrorMessage = '';

export function didNeonFail() {
  return neonFailedFlag;
}

export function getNeonErrorMessage() {
  return neonErrorMessage;
}

export function resetNeonFailure() {
  neonFailedFlag = false;
  neonErrorMessage = '';
}

/**
 * Get all events
 * - ALWAYS calls the API at runtime
 * - NO silent mock fallback
 * - Errors are surfaced, not hidden
 */
export async function getEvents() {
  const source = getDataSource();
  const apiBaseUrl = getApiBaseUrl();
  const requestUrl = `${apiBaseUrl}/events?source=${source}`;
  
  // REQUIRED: Hard proof logging
  console.log('='.repeat(60));
  console.log('[Events API] FETCH REQUEST');
  console.log('='.repeat(60));
  console.log('[Events API] Full URL:', requestUrl);
  console.log('[Events API] API Base:', apiBaseUrl);
  console.log('[Events API] Source:', source);
  console.log('[Events API] Window URL:', window.location.href);
  console.log('='.repeat(60));
  
  try {
    // Always call the API with explicit source parameter
    const response = await apiClient.get(`/events?source=${source}`);
    
    // Check if API returned an error
    if (!response.success) {
      throw new Error(response.error?.message || 'API returned error');
    }
    
    console.log('='.repeat(60));
    console.log('[Events API] SUCCESS');
    console.log('[Events API] Event Count:', response.data?.length || 0);
    console.log('[Events API] Source Returned:', response.meta?.source);
    console.log('='.repeat(60));
    
    // Reset failure flag on success
    neonFailedFlag = false;
    neonErrorMessage = '';
    
    return {
      success: true,
      data: response.data || [],
      meta: response.meta,
      _source: source,
      _requestUrl: requestUrl
    };
    
  } catch (error) {
    console.error('='.repeat(60));
    console.error('[Events API] FAILED');
    console.error('[Events API] Error:', error.message);
    console.error('[Events API] Request URL:', requestUrl);
    console.error('='.repeat(60));
    
    // Set failure flag
    neonFailedFlag = true;
    neonErrorMessage = error.message || 'Failed to fetch events from API';
    
    // DO NOT silently return mock data
    // Return error so UI can show error state
    return {
      success: false,
      data: [],
      error: { 
        message: neonErrorMessage,
        source: source,
        apiBaseUrl: apiBaseUrl,
        requestUrl: requestUrl
      },
      _failed: true
    };
  }
}

/**
 * Get single event by ID
 */
export async function getEvent(eventId) {
  const source = getDataSource();
  const apiBaseUrl = getApiBaseUrl();
  
  console.log(`[Events API] Fetching event ${eventId} with source=${source} from ${apiBaseUrl}`);
  
  try {
    const response = await apiClient.get(`/events/${eventId}?source=${source}`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'API returned error');
    }
    
    return {
      success: true,
      data: response.data,
      meta: response.meta,
      _source: source
    };
    
  } catch (error) {
    console.error(`[Events API] FAILED to fetch event ${eventId}:`, error);
    
    neonFailedFlag = true;
    neonErrorMessage = error.message || 'Failed to fetch event from API';
    
    // Return error, not mock data
    return {
      success: false,
      data: null,
      error: { message: neonErrorMessage }
    };
  }
}

/**
 * Create event (admin only)
 */
export async function createEvent(eventData) {
  return await apiClient.post('/events', eventData);
}

/**
 * Update event (admin only)
 */
export async function updateEvent(eventId, eventData) {
  return await apiClient.patch(`/events/${eventId}`, eventData);
}

/**
 * Delete event (admin only)
 */
export async function deleteEvent(eventId) {
  return await apiClient.delete(`/events/${eventId}`);
}

export default {
  getEvents,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  didNeonFail,
  getNeonErrorMessage,
  resetNeonFailure,
};
